import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Sparkles } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  clearAll,
  fetchNotifications,
  hrefForSubject,
  markAllRead,
  markRead,
  type NotificationRow,
} from '../api/notifications'
import { reassertThisDevice, registerWorker, showBadge } from '../api/push'
import { NoteInterval, noteExpected } from '../api/fanOut'
import { useCan, useSession } from '../useSession'

/**
 * What just happened (D-324).
 *
 * **One of three surfaces, and the narrowest.** A rail badge answers *where*;
 * Home answers *what should I do now*; this answers *what just happened*, and
 * carries what has no destination of its own — a private-lesson request, a
 * standing alert. Nothing here is also counted on the rail, which is the rule
 * D-324 reversed D-167's summing to make possible.
 *
 * **Unread, never outstanding.** D-285 stores read state and refuses to store
 * resolution: whether a request still needs answering is a property of the
 * request. So this number falls when she looks, and something read but not dealt
 * with stays on Home. A badge that cannot reach zero is one people stop seeing
 * (D-149).
 *
 * **Not on a classroom tablet.** The endpoint refuses a kiosk by default
 * (D-233); this refuses to render, so the tablet has no control that fails.
 */
export default function NotificationBell() {
  const { session } = useSession()
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canViewPeople = useCan('ViewPeople')

  const signedIn = Boolean(session) && session?.isKiosk !== true

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    staleTime: 60_000,
    enabled: signedIn,
    /*
     * <b>While a fan-out is telling families, keep looking</b> (D-869).
     *
     * Telling a class is handed off after the response and the screen promises
     * *"you will get a note when they have all been told"*. Everything else
     * about this query is deliberately quiet - the comment on the button below
     * explains why - and that quietness is exactly what would have made the
     * promise false: the row lands in the database and the badge does not move,
     * because nothing asks. The old slow spinner at least said when the send
     * had finished.
     *
     * <b>Only while one is owed</b>, which is a four-minute window after a
     * hand-off rather than a permanent poll. QA's send rate is eight times
     * slower than production's (ten a minute against eighty), so the window has
     * to cover the slow host, and the ordinary case is nothing at all.
     */
    refetchInterval: () => (noteExpected() ? NoteInterval : false),
  })

  const read = useMutation({
    mutationFn: markRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const readAll = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  /*
   * Clearing is not marking read, and having only the latter is what was
   * reported (D-391). Read answers "have I seen this"; the list still held
   * every row afterwards, so a month of use is a wall of things already dealt
   * with — and a list nobody can empty is one nobody reads.
   */
  const clear = useMutation({
    mutationFn: clearAll,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  /*
   * **The app-icon badge, kept in step with this number** (D-530).
   *
   * The push payload carries the count so a closed app can set it; this is the
   * other half, for while she is looking at the app. Both read the same
   * definition of "unread" (D-528), which is what stops the icon saying three
   * while the bell shows one — a difference that reads to a parent as a message
   * gone missing, and that neither side could notice on its own.
   *
   * **Android has no badge to set, and that is a platform limit.** Its badges
   * are derived from tray notifications; the API can only suppress one. So this
   * call is absent there rather than failing, which is why it is guarded rather
   * than feature-detected once.
   */
  useEffect(() => {
    if (!signedIn || data === undefined) return
    showBadge(data.unread)
  }, [signedIn, data])

  /*
   * **Registered on load, and it asks for nothing.** Registering a worker shows
   * no prompt and sends no notification — permission is a separate act, on the
   * account screen, from a tap. What it buys is `pushsubscriptionchange`: a
   * browser can replace a subscription without anybody asking, and if no worker
   * is registered when it does, the phone goes quiet with every signal saying
   * it is still subscribed.
   *
   * **And the same sentence is why the studio is told again** (D-892). That
   * failure does not need a missing worker to happen - an archived row or a
   * missed event produces it too, and nothing on either side can see it. Here
   * rather than on `/account`, because this mounts on every signed-in page and
   * the repair is worth most to somebody who has no reason to open her
   * settings; and never in `main.tsx`, which runs before anybody has signed in.
   */
  useEffect(() => {
    if (!signedIn) return

    void registerWorker().then(() => reassertThisDevice())
  }, [signedIn])

  /*
   * **The worker asks the app to route, when it cannot route the app itself**
   * (D-530). `WindowClient.navigate` is missing on WebKit, which is the very
   * platform this feature is built around — without this, tapping a banner with
   * the app already open focuses whatever screen she was last on and drops the
   * message entirely.
   */
  useEffect(() => {
    if (!signedIn || !('serviceWorker' in navigator)) return

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | null
      if (data?.type !== 'navigate' || typeof data.url !== 'string') return

      // Same-origin paths only. The worker is ours, but a `navigate` that
      // accepted anything would be one postMessage from being a redirect.
      if (data.url.startsWith('/') && !data.url.startsWith('//')) navigate(data.url)
    }

    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [signedIn, navigate])

  // Escape and a click outside, the same as every other menu here.
  useEffect(() => {
    if (!open) return

    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    const onClick = (e: MouseEvent) => {
      if (!container.current?.contains(e.target as Node)) setOpen(false)
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)

    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  if (!signedIn) return null

  const rows = data?.rows ?? []
  const unread = data?.unread ?? 0

  async function openRow(row: NotificationRow) {
    /*
     * Read first, then go. Marking after navigating loses the mutation when the
     * panel unmounts, which is how a bell keeps a number nobody can clear.
     */
    if (!row.isRead) read.mutate(row.id)

    setOpen(false)

    /*
     * **The exact place if it can be found, the screen if not** (D-392).
     *
     * `asked`: *"for privates that are confirmed it brings you to the private
     * on your schedule."* Which screen is a fact about the message; which week
     * and which dancer are live state, so they are resolved now rather than
     * stored when it was sent — a teacher can move a private after confirming
     * it, and a link minted on Monday would point at an empty week.
     *
     * **The floor never moves.** `hrefForSubject` always answers, so a
     * withdrawn request or a failed lookup lands on the screen where the answer
     * would be rather than nowhere at all — which is the invariant the bell is
     * for.
     */
    const href = hrefForSubject(row, { canViewPeople, isParent: session?.isParent, teaches: session?.teaches })

    if (href) navigate(href)
  }

  return (
    /*
     * **Not positioned on a phone, so the panel hangs from the header row.** The
     * panel is 320px and was pinned to the bell's right edge, and on a 390px phone
     * the header wraps the bell to the middle of its row - so the panel began 74px
     * off the left of the screen, and every row lost its first words, *What's new*
     * included (measured in Edge, 2026-09-17). From `sm` up the bell sits at the
     * right and the panel stays under it.
     */
    <div ref={container} className="sm:relative">
      <button
        type="button"
        onClick={() => {
          /*
           * Refetch on open, because a bell promises more than a badge does.
           * Nothing else triggers one: this lives in the header and never
           * unmounts, and `refetchOnWindowFocus` is off globally - so a tab left
           * open through a class night would still read zero at half past eight.
           */
          if (!open) refetch()
          setOpen((o) => !o)
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        /*
         * The count is in the name, not only in the dot. A screen reader is the
         * one reader for whom a coloured circle says nothing at all.
         */
        aria-label={unread > 0 ? `Notifications — ${unread} unread` : 'Notifications'}
        title={unread > 0 ? `${unread} unread` : 'Notifications'}
        className="relative grid size-8 place-items-center rounded-lg text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink"
      >
        <Bell size={17} strokeWidth={2} />

        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 grid min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-bold text-on-accent ring-2 ring-surface tabular-nums"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-full right-5 left-5 z-50 mt-1.5 rounded-xl border border-line bg-surface p-1.5 shadow-xl sm:right-0 sm:left-auto sm:w-80"
        >
          <div className="flex items-center justify-between px-2.5 pt-1 pb-2">
            <p className="text-[10.5px] font-bold tracking-[0.1em] text-ink-3 uppercase">
              Notifications
            </p>

            {/*
              **Both controls, and each appears only when it would do
              something** (D-391).

              *Mark all read* is keyed on unread and *Clear all* on there being
              any rows at all — because the case that prompted this is exactly
              the one where they differ: everything read, nothing gone, and the
              only control on offer already done. A button that cannot change
              anything is one people learn to ignore.
            */}
            <div className="flex items-center gap-3">
              {unread > 0 && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => readAll.mutate()}
                  className="text-[12px] font-semibold text-accent hover:underline"
                >
                  Mark all read
                </button>
              )}

              {rows.length > 0 && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => clear.mutate()}
                  disabled={clear.isPending}
                  className="text-[12px] font-semibold text-ink-3 hover:underline disabled:opacity-60"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/*
            **What's new, for a while after a release** (D-731). A fixed link
            rather than a notification row: nothing is sent, nothing is counted,
            and it goes of its own accord. It is not one of her notifications,
            so it sits above them with a rule under it rather than among them.
          */}
          {session?.whatsNewLink && (
            <div className="mb-1 border-b border-line pb-1">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  navigate('/whats-new')
                }}
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] font-semibold text-ink transition-colors hover:bg-surface-2"
              >
                <Sparkles aria-hidden="true" className="size-4 shrink-0 text-accent" />
                What&rsquo;s new in the app
              </button>
            </div>
          )}

          {/*
            Three states, because "none" and "could not ask" are different
            facts and only one of them can be described (D-209).

            **This is the normal case, not the rare one**: every
            database-backed endpoint refuses for about 75 seconds after a
            deploy and `retry: 1` gives up in a second, so a parent with three
            unread was being told confidently that she had none.
          */}
          {isPending && (
            <p className="px-2.5 py-6 text-center text-[13px] text-ink-3">Loading&hellip;</p>
          )}

          {isError && (
            <p className="px-2.5 py-6 text-center text-[13px] text-crit">
              Could not load these just now. Please try again.
            </p>
          )}

          {!isPending && !isError && rows.length === 0 && (
            <p className="px-2.5 py-6 text-center text-[13px] text-ink-3">
              Nothing yet. Anything the studio sends you turns up here.
            </p>
          )}

          <div className="max-h-80 overflow-y-auto">
            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                role="menuitem"
                onClick={() => openRow(row)}
                className="block w-full rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-surface-2"
              >
                <span className="flex items-start gap-2">
                  {/*
                    Unread is marked, not coloured differently — a whole row in a
                    second colour reads as a different kind of thing rather than
                    as a newer one.
                  */}
                  <span
                    aria-hidden="true"
                    className={`mt-1.5 size-1.5 shrink-0 rounded-full ${
                      row.isRead ? 'bg-transparent' : 'bg-accent'
                    }`}
                  />

                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold wrap-anywhere text-ink">
                      {row.title}
                    </span>
                    {row.body && (
                      <span className="mt-0.5 block text-[12px] wrap-anywhere text-ink-3">
                        {row.body}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
